<template>
  <div class="products">
    <h4 class="main-header">{{ $t('products') }}</h4>
    <div v-if="companySetupError" class="empty-state-ctn mt-5">
      <div class="py-4 font-weight-medium">
        {{ $t('company_setup_error') }} {{ $t('contact_support_for_help') }}
      </div>
    </div>
    <v-row v-else class="ma-0">
      <v-col class="product-list flex-grow-1" :class="{ 'pl-2': campaigns.length == 1}">
       <div class="product-actions-ctn d-flex flex-wrap mb-5 pt-2 align-center">
          <div class="mr-3 order-first">
            <v-btn
              @click="openCreateProductModal"
              :disabled="usedCodesCount >= totalCodesCount"
              class="md-raised primary"
            >
              <v-icon class="mr-2">mdi-plus</v-icon>{{ $t('new_product') }}
            </v-btn>
          </div>

          <div class="mr-3" :class="$vuetify.breakpoint.smAndDown ? 'ml-auto order-2' : 'ml-3 order-3'">
            <codes-quota></codes-quota>
          </div>

          <div
            class="search-wrapper"
            :class="$vuetify.breakpoint.smAndDown ? 'order-3 w-100 mt-3' : 'flex-grow-1 order-2'"
          >
            <v-form  v-if="totalCodesCount > 0" @submit.prevent class="w-100">
              <v-text-field
                :label="$t('search_product_placeholder')"
                solo
                hide-details
                dense
                append-icon="mdi-magnify"
                v-model="search"
              ></v-text-field>
            </v-form>
          </div>
        </div>
        <div v-if="products.length || search" class="product-table">
          <v-row class="product-table-header font-light pa-0 ma-0">
            <v-col>{{$t('products')}}</v-col>
            <!-- <v-col class="d-flex justify-center" :cols="2">{{$t('gtin')}}</v-col> -->
            <v-col v-if="!$vuetify.breakpoint.smAndDown" class="d-flex justify-start" :cols="5">{{$t('url_and_gs1_dl')}}</v-col>
            <v-col class="d-flex justify-start" :cols="$vuetify.breakpoint.smAndDown ? 5 : 2">{{$t('qr_code')}}</v-col>
            <v-col class="d-flex justify-center" :cols="!$vuetify.breakpoint.smAndDown ? 1 : 2">{{$t('actions')}}</v-col>
          </v-row>
          <v-progress-linear v-if="loadingProducts" indeterminate></v-progress-linear>
          <v-row class="pa-0 ma-0 product-table-item" v-show="!loadingProducts" v-for="product in products" :key="product.id">
            <v-col class="product-cell" align-self="center" @click="goToProductDetails(product, 'info')">
              <v-row class="pa-0 ma-0" align="center">
                <v-col class="product-image-ctn pa-0" v-if="product.image">
                  <img class="product-image" :src="product.image">
                </v-col>
                <v-col class="product-name-ctn">
                  <span class="font-medium mr-2">{{product.name}}</span>
                  <span v-if="!$vuetify.breakpoint.smAndDown" class="product-sku">{{product.sku}}</span>
                </v-col>
              </v-row>
            </v-col>
            <v-col class="text-break" v-if="!$vuetify.breakpoint.smAndDown" @click="goToProductDetails(product, 'gs1')" :cols="5" align-self="center">
              {{systemPrefix}}{{product.code?.message}}
            </v-col>
            <v-col align-self="center" @click="goToProductDetails(product, 'qr')" :cols="$vuetify.breakpoint.smAndDown ? 5 : 2">
              <v-row class="pa-0 ma-0" align="center">
                <div class="qr-preview-ctn" v-if="product.code">
                  <img class="qr" :src="previewUrl(product)">
                  <img v-if="qrTemplates[product.id] && qrTemplates[product.id].image" :style="{backgroundColor: qrTemplates[product.id].background}" class="logo" :src="toDataUrl(qrTemplates[product.id].image)">
                </div>
              </v-row>
            </v-col>
            <v-col align-self="center" class="text-center" :cols="!$vuetify.breakpoint.smAndDown ? 1 : 2">
              <v-menu bottom left>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn icon v-bind="attrs" v-on="on" color="primary">
                    <v-icon>mdi-dots-vertical</v-icon>
                  </v-btn>
                </template>
                <v-list>
                  <v-list-item @click="goToProductDetails(product, 'info')" link>
                    <v-list-item-title>{{ $t('edit_product_information') }}</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="goToProductDetails(product, 'gs1')" link>
                    <v-list-item-title>{{ $t('manage_gs1_digital_link') }}</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="goToProductDetails(product, 'qr')" link>
                    <v-list-item-title>{{ $t('edit_qr_code_style')}}</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="goToProductDetails(product, 'redirection')" link>
                    <v-list-item-title>{{ $t('manage_scan_destination')}}</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </v-col>
          </v-row>
          <v-row class="pa-2 ma-0 product-table-item" v-show="!loadingProducts && !products.length">
            {{$t('no_product_found')}}
          </v-row>
        </div>
        <div v-else-if="!loadingProducts && !search" class="empty-state-ctn">
          <img class="main-image" src="@/assets/empty-state-products.svg" alt="">
          <div class="welcome">{{$t('empty_state_welcome')}}</div>
          <div class="product-info">
            <div class="create-product">{{$t('empty_state_create_product')}}</div>
            <v-btn
              @click="openCreateProductModal"
              :disabled="usedCodesCount >= totalCodesCount"
              class="md-raised primary">
              <v-icon class="mr-2">mdi-plus</v-icon>{{ $t('new_product') }}
            </v-btn>
          </div>
        </div>
        <!-- <div v-else-if="!loadingProducts && !search && totalCodesCount === 0" class="trial-empty-state-ctn">
          <div class="billing-cycle-selector d-flex justify-center mb-5">
            <div class="wrapper font-weight-medium">
              <button class="px-4 py-2" :class="{ 'selected': billingCycle === 'month' }" @click="updateBillingCycle('month')">{{ $t('monthly') }}</button>
              <button class="px-4 py-2" :class="{ 'selected': billingCycle === 'year' }" @click="updateBillingCycle('year')"> {{ $t('yearly_save_percent', { percent: 25 }) }}</button>
            </div>
          </div>
          <div class="trial-title font-weight-bold">{{ $t('empty_state_trial_title') }}</div>
          <div class="price-ctn">
            <div v-if="isLoading" class="loading-price">
              <v-progress-circular size="24" indeterminate color="primary"></v-progress-circular>
            </div>
            <div v-else class="price font-weight-medium">{{prices['st-pro'][billingCycle] || '-'}}</div>
            <div class="price-description font-weight-light">{{ billingCycle === 'month' ? $t('per_month_vat_excluded') : $t('yearly_savings', { amount: getYearlySavings('st-pro') || '-' }) }}</div>
          </div>
          <div class="details-box">
            <v-row class="pt-5 font-weight-medium ma-0" align="center" justify="start">
              <qr-icon size="24" class="mr-2" color="#4b99ca"></qr-icon>
              <span class="mr-1 font-weight-bold">{{getPlan('st-pro').quota}}</span>
              {{$t('qr_codes')}}
            </v-row>
            <v-row class="pt-5 font-weight-medium ma-0" align="center" justify="start">
              <seat-icon size="24" class="mr-2" color="#4b99ca"></seat-icon>
              <span class="mr-1 font-weight-bold">{{getPlan('st-pro').seats}}</span>
              {{$t('team_members')}}
            </v-row>
            <v-row class="pt-5 font-weight-medium ma-0" align="center" justify="start">
              <product-scan-icon size="24" class="mr-2" color="#4b99ca"></product-scan-icon>
              <span class="mr-1 font-weight-bold">{{$t('unlimited')}}</span>
              {{$t('scans')}}
            </v-row>
          </div>
          <div>
            <v-btn class="my-4" @click="upgrade('st-pro')" :disabled="currentPlan?.id === 'st-pro'" :loading="!customer.id" color="primary">{{currentPlan?.id === 'st-pro' ? $t('current_plan') : $t('subscribe')}}</v-btn>
            <br>
            <v-btn @click="goToPlans()" text color="primary">{{$t('view_all_plans')}}</v-btn>
          </div>
        </div> -->

        <div class="pagination-ctn mt-5 mb-4">
          <v-pagination v-show="!loadingProducts && filteredProductCount > 0" color="primary" :total-visible="7" v-model="page" :length="nbPages">
          </v-pagination>
        </div>
      </v-col>
    </v-row>
    <!--  -->
    <v-dialog v-model="productDialog" width="1000" :persistent="true">
      <product-creation
        ref="productCreation"
        :campaign="this.selectedCampaign?.id"
        @on-product-created="onProductCreated"
        @on-close-dialog="handleCloseDialog()">
      </product-creation>
    </v-dialog>

    <checkout-dialog
      v-if="profile?.company?.id && isUpgradePlanOpen"
      :openModal="isUpgradePlanOpen"
      :selectedPlan="selectedPlan"
      ref="paddle-checkout-dialog"
      @close="handleCheckoutPopupClose"
      :isYearlyPayment="billingCycle === 'year'"
      >
    </checkout-dialog>
  </div>
</template>

<script>
import Api from '@/api'
import ProductCreation from '@/components/forms/ProductCreation'
import CodesQuota from '@/components/CodesQuota'
import Plans from '@/const/plans'
import { mapGetters, mapMutations } from 'vuex'
import Utils from '@/utils/utils'
import amplitude from 'amplitude-js'
import paddleService from '@/services/paddle'
import CheckoutDialog from '@/components/upgrade/CheckoutDialog'

export default {
  name: 'products',
  components: {
    ProductCreation,
    CodesQuota,
    CheckoutDialog
  },
  data () {
    return {
      companySetupError: false,
      isUpgradePlanOpen: false,
      isLoading: false,
      selectedProduct: {},
      qrTemplates: {},
      unpublishing: false,
      billingCycle: 'month',
      productDialog: false,
      debounceTimeout: null,
      pageSize: 10,
      page: 1,
      search: '',
      code: {},
      plans: Plans,
      prices: {
        'st-pro': {
          month: null,
          year: null
        }
      },
      loadedPlans: {
        'st-pro': {}
      },
      selectedPlan: null,
      removeEventCallback: null
    }
  },
  watch: {
    '$route.params' () {
      if (this.$route.name === 'products.edit' || this.$route.name === 'products.new') {
        this.productDialog = true
      }
    },

    search () {
      clearTimeout(this.debounceTimeout)
      this.debounceTimeout = setTimeout(() => {
        this.filterProducts()
      }, 200)
    },

    page (newVal) {
      this.loadProducts(newVal - 1)
    }
  },
  computed: {
    ...mapGetters(['campaigns', 'products', 'selectedCampaign', 'loadingProducts', 'filteredProductCount', 'totalCodesCount', 'usedCodesCount', 'prefixesLoaded', 'brands', 'isDefaultRedirectionRuleLoaded', 'defaultRedirectionRule', 'profile', 'currentPlan', 'systemPrefix', 'customer']),

    nbPages () {
      return Math.ceil(this.filteredProductCount / this.pageSize)
    }

  },
  methods: {
    ...mapMutations({
      setSelectedCampaign: 'SET_SELECTED_CAMPAIGN',
      setSelectedProduct: 'SET_SELECTED_PRODUCT',
      setScmLoaded: 'SET_SCM_LOADED'
    }),

    isBase64StringSVG (base64String) {
      const decodedData = atob(base64String)
      return decodedData.startsWith('<?xml version=') && decodedData.includes('<svg')
    },

    toDataUrl (base64) {
      if (base64) {
        if (base64.indexOf('data:image') === 0) {
          return base64
        } else if (!this.isBase64StringSVG(base64)) {
          return `data:image/png;base64,${base64}`
        } else {
          return `data:image/svg+xml;base64,${base64}`
        }
      }
    },

    previewUrl (product) {
      if (product.code && this.qrTemplates[product.id]?.color) {
        return `${Api.qr_service_url}qr/single/?error_correction=q&color=%23${this.qrTemplates[product.id].color.split('#')[1]}&background=%23${this.qrTemplates[product.id].background.split('#')[1]}&content=${this.systemPrefix}${product.code.message}${product.code.scm_data.gs1_data_attributes ? `?${product.code.scm_data.gs1_data_attributes}` : ''}`
      } else if (product.code) {
        return `${Api.qr_service_url}qr/single/?error_correction=q&content=${this.systemPrefix}${product.code.message}${product.code.scm_data.gs1_data_attributes ? `?${product.code.scm_data.gs1_data_attributes}` : ''}`
      }
      return ''
    },

    updateBillingCycle (cycle) {
      this.billingCycle = cycle
    },

    getPlan (planId) {
      return this.plans.find((plan) => plan.id === planId)
    },

    getYearlySavings (planId) {
      return paddleService.calculateYearlySavings(this.loadedPlans[planId])
    },

    async updatePrices () {
      this.isLoading = true
      try {
        const { prices, loadedPlans } = await paddleService.getPricePreviews(this.usedCodesCount > 0)
        this.prices = prices
        this.loadedPlans = loadedPlans
      } catch (error) {
        console.error(`Error updating prices: ${error.message}`)
      } finally {
        this.isLoading = false
      }
    },

    upgrade (planID) {
      this.isUpgradePlanOpen = true
      const plan = this.plans.find((plan) => plan[this.billingCycle] === Api.paddle_plans[planID][this.billingCycle])
      plan.price = this.prices[planID][this.billingCycle]
      plan.productID = plan[this.billingCycle]
      this.selectedPlan = plan
    },

    async handleCheckoutPopupClose (isPaymentFullfilled) {
      this.isUpgradePlanOpen = false
      if (isPaymentFullfilled) {
        const user = await this.$store.dispatch('loadUserProfile')
        await this.$store.dispatch('loadUserContract', { id: user.plan?.contract_id })
        await this.$store.dispatch('loadQuotas')
      }
    },

    goToProductDetails (product, tab) {
      this.$router.push({ name: `products.${tab}`, params: { id: product.id, tab: tab, product: product } })
    },

    goToPlans () {
      this.$router.push({ name: 'upgrade' })
    },

    isGS1Code (code) {
      return Utils.isGS1Identifier(code)
    },

    goToLandingPage (id) {
      this.$router.push({ name: 'landing-page.edit', params: { id: id } })
    },

    filterProducts () {
      this.loadProducts()
    },

    openCreateProductModal () {
      this.$router.push({ name: 'products.new', params: { campaign: this.selectedCampaign.id } })
    },

    goToManageAll () {
      amplitude.getInstance().logEvent('click manage all qr codes')
    },

    manageCodes (product) {
      amplitude.getInstance().logEvent('click get qr codes button')
      this.$router.push({ name: 'products.codes', params: { id: product.id, product: product } })
    },

    async loadProducts (page) {
      await this.$store.dispatch('loadProducts', { campaign: this.selectedCampaign.id, offset: page ? page * this.pageSize : 0, limit: this.pageSize, search: this.search })

      if (this.products && this.products.length) {
        this.products.forEach((product) => {
          this.$store.dispatch('loadProductCodeCount', { product: product.id })
          if (!this.qrTemplates[product.id]) {
            this.$store.dispatch('loadProductJson', { id: product.id }).then((res) => {
              if (res.json.scantrust_qr_template) {
                this.$store.dispatch('loadQrTemplate', {
                  id: res.json.scantrust_qr_template,
                  product: res.product
                }).then((response) => {
                  this.$set(this.qrTemplates, response.product, response.template)
                })
              }
            })
          }
        })
      }
    },

    async onProductCreated ({ product, gtin }) {
      if (this.usedCodesCount >= this.totalCodesCount && !product.code) {
        this.$refs.productCreation.stopSaving()
        return
      }

      let code = product.code
      let destinationId = product.stc_data?.scantrust_destination || null

      try {
        if (!product.code) {
          code = await this.$store.dispatch('generateOnDemandCode', { product: product })
          // Keep the local product in step with what was just created: a failed GS1
          // rename sends the user back to step 1, and the retry must not generate
          // a second code or a second destination.
          product.code = code
        }

        // New products start on a DPP destination. No landing page is created here:
        // the creation dialog's second step, or the product's page-after-scan tab,
        // creates one on demand if the user picks a custom landing page.
        if (!destinationId) {
          const defaultDestination = {
            name: product.sku,
            conditions: { or: [{ and: [{ '==': [{ var: 'product_id' }, product.id] }] }] },
            position: 0,
            type: 'dpp',
            url: Api.dpp_landing_page_url,
            stc_config: null
          }

          const destination = await this.$store.dispatch('createRuleDestination', { ruleId: this.defaultRedirectionRule.id, destination: defaultDestination })
          destinationId = destination.id
          await this.$store.dispatch('updateProductJson', { id: product.id, json: { scantrust_destination: destinationId } })
          product.stc_data = { ...(product.stc_data || {}), scantrust_destination: destinationId }
        }
      } catch (err) {
        // The product already exists at this point. Surface the failure and let the
        // user retry from step 1 instead of leaving the NEXT button loading forever.
        console.error(err)
        this.$refs.productCreation.stopSaving()
        this.$store.dispatch('showSnackBar', { message: this.$t('product_setup_failed') })
        return
      }

      if (gtin) {
        const gs1Id = `01/${gtin}`
        try {
          await this.$store.dispatch('renameCode', { product: product, source: code, gs1Id: gs1Id, tags: { gs1_gtin: gtin } })
        } catch (err) {
          this.$refs.productCreation.handleSubmitError(err)
          return
        }
      }

      this.$refs.productCreation.goToDestinationStep({ product, destinationId })
    },

    handleCloseDialog () {
      this.$refs.productCreation.resetForm()
      this.productDialog = false
      this.$router.push({ name: 'products' })
    },

    getProductImage (product) {
      return product.image ? product.image : product.brand && product.brand.image ? product.brand.image : require('@/assets/product-placeholder.png')
    },

    selectCampaign (campaign) {
      amplitude.getInstance().logEvent('select campaign')
      this.setSelectedCampaign(campaign)
      this.setScmLoaded(false)
      this.loadProducts()
      this.$store.dispatch('loadSelectedCampaignConsumerOptions', campaign.id)
    }
  },
  async mounted () {
    paddleService.initialize()

    this.removeEventCallback = paddleService.addEventCallback((event) => {
      if (this.$refs['paddle-checkout-dialog']) {
        this.$refs['paddle-checkout-dialog'].eventHandler(event)
      }
    })

    this.updatePrices()
  },
  beforeDestroy () {
    if (this.removeEventCallback) {
      this.removeEventCallback()
    }
  },
  async created () {
    if (!this.campaigns.length) {
      const campaign = await this.$store.dispatch('loadCampaigns')
      if (campaign) {
        this.selectCampaign(campaign)
      } else {
        this.companySetupError = true
      }
    } else {
      this.selectCampaign(this.campaigns[0])
    }

    if (!this.prefixesLoaded) {
      this.$store.dispatch('loadPrefixes')
    }

    if (!this.isDefaultRedirectionRuleLoaded) {
      this.$store.dispatch('loadDefaultRedirectionRule')
    }

    if (!this.brands.length) {
      this.$store.dispatch('loadBrands')
    }

    if (this.$route.name === 'products.edit' || this.$route.name === 'products.new') {
      this.productDialog = true
    }
  }
}
</script>

<style scoped lang="sass">

@import "@/variables.sass"

.product-image-ctn
  max-width: 75px
  max-height: 75px
  .product-image
    height: 75px
    width: 75px
    object-fit: contain

.qr-preview-ctn
  position: relative
  .qr
    width: 75px
    height: 75px
  .logo
    position: absolute
    width: 24px
    padding: 2px
    top: calc(50% - (24px / 2))
    left: calc(50% - (24px / 2))
    background: white

.empty-state-ctn
  display: flex
  flex-direction: column
  align-items: center
  gap: 30px
  padding: 20px 0
  color: $text-black
  text-align: center
  letter-spacing: 0.8px
  .main-image
    width: 255px
    height: 221px
  .welcome
    font-size: 52px
    line-height: 43px
  .product-info
    display: flex
    flex-direction: column
    align-items: center
    gap: 20px
  .create-product
    font-size: 24px
    line-height: 24px

.trial-empty-state-ctn
  width: 85%
  margin-left: auto
  margin-right: auto
  color: #000
  text-align: center
  letter-spacing: 0.8px
  margin-top: 25px
  .trial-description
    font-size: 20px
  .trial-title
    margin: 12px 0px
    font-size: 26px
  .price-ctn
    font-size: 36px
    .price-description
      font-size: 16px

.product-table
  .product-table-header
    border-radius: 6px 0px 0px 0px
    border: 1px solid rgba(0, 0, 0, 0.10)
    background: #F3F3F3

  .product-table-item
    cursor: pointer
    border-bottom: 1px solid rgba(0, 0, 0, 0.10)
    border-left: 1px solid rgba(0, 0, 0, 0.10)
    border-right: 1px solid rgba(0, 0, 0, 0.10)
    background: #FFF
    .product-sku
      font-size: 14px

    // A long product name or SKU is a single unbreakable token. Without this the
    // cell keeps its min-content width and the row wraps the QR and actions cells
    // onto a second line, so let the text break and the cells shrink.
    .product-cell,
    .product-name-ctn
      min-width: 0

    .product-name-ctn
      overflow-wrap: anywhere

.pointer
  cursor: pointer

.elabel-status
  .status-pill
    display: inline-block
    width: 16px
    height: 16px
    margin-right: 4px
    border-radius: 50%
    background-color: #D9D9D9
    &.draft
      background-color: #F5A623
    &.published
      background-color: #3FC135

.products
  margin-right: auto

  .link
    cursor: pointer
    color: $primary

.product-list
  padding-top: 14px
  padding-left: 14px
  overflow-y: auto
  height: calc(100vh - 48px - var(--banner-height, 0px))

.billing-cycle-selector
  .wrapper
    border-radius: 4px
    background: #F1F1F1
    button
      margin: 3px
      &.selected
        color: $primary
        background: white

.details-box
  display: inline-block

</style>
